"use client";

import React from "react";
import { UseFormReturn, useWatch } from "react-hook-form";

import {
	Box,
	CardContent,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from "@mui/material";

import { Field } from "@onair/minimal-ui/hook-form";
import { useResponsive } from "@onair/minimal-ui/hooks/use-responsive";

import { ExtendRoomProvider } from "@onair/constants";
import { RHFInputDecimalField } from "@onair/ui/form-controls/input-decimal";
import dayjs from "@onair/utils/dayjs";
import CardWizard from "./card-wizard.tsx";
import EventRoomSelect from "./event-room-select.tsx";
import { WizardSchemaType } from "./index.tsx";
import InputLabelWizard from "./input-label-wizard.tsx";
import PreviewInfo from "./preview/info.tsx";
import SocialCommunitySection from "./social-community-section.tsx";
import TicketTierForm from "./ticket-tiers";
import { LocationType } from "@onair/repositories";
import { Iconify } from "@onair/minimal-ui/iconify";
import { AddressField } from "./address/address-scope-box.tsx";

interface PropTypes {
	methods: UseFormReturn<WizardSchemaType>;
	navigator?: React.ReactNode;
}

const PRICING_ONLINE_PREFIX_NAME = "stepTimelineAndPricing.ticketTiers.0";

const StepTimelineAndPricing: React.FC<PropTypes> = ({
	methods,
	navigator,
}) => {
	const { unregister, setValue, getValues, control } = methods;

	const mdDown = useResponsive("down", "md");

	const locationType = useWatch({
		name: "locationType",
		control,
	});

	const startDate = useWatch({
		name: "stepTimelineAndPricing.startDate",
		control,
	});

	const roomProvider = useWatch({
		name: "stepTimelineAndPricing.roomProvider",
		control,
	});

	const isFree = useWatch({
		name: `${PRICING_ONLINE_PREFIX_NAME}.isFree`,
		control,
	});
	const isUnlimitedAudiences = useWatch({
		name: `${PRICING_ONLINE_PREFIX_NAME}.isUnlimitedAudiences`,
		control,
	});
	const hasBeenPaid = getValues(`${PRICING_ONLINE_PREFIX_NAME}.hasBeenPaid`);
	const isOffline = locationType === LocationType.OFFLINE;

	React.useEffect(() => {
		if (isFree) {
			unregister(`${PRICING_ONLINE_PREFIX_NAME}.farePrice`);
			setValue(`${PRICING_ONLINE_PREFIX_NAME}.farePrice`, 0);

			if (
				getValues("stepTimelineAndPricing.roomProvider") ===
				ExtendRoomProvider.SYSTEM
			) {
				unregister("stepTimelineAndPricing.roomProvider");
				// @ts-ignore
				setValue("stepTimelineAndPricing.roomProvider", null);
			}
		}

		if (isUnlimitedAudiences) {
			setValue(`${PRICING_ONLINE_PREFIX_NAME}.limitAudiences`, undefined, {
				shouldValidate: true,
			});
		}
	}, [isFree]);

	const data = useWatch({
		control,
	});

	return (
		<Stack direction="row" spacing={3}>
			<Box className="flex flex-col gap-6" flex={1}>
				<CardWizard>
					<CardContent>
						<Stack spacing={3}>
							<Stack spacing={2}>
								<InputLabelWizard>Thời gian diễn ra</InputLabelWizard>
								<Stack
									sx={{
										flexDirection: {
											xs: "column",
											md: "row",
										},
									}}
									spacing={2}
								>
									<Field.DateTimePicker
										name="stepTimelineAndPricing.startDate"
										label="Thời gian bắt đầu"
										slotProps={{
											textField: {
												required: true,
											},
										}}
										disablePast
									/>
									<Field.DateTimePicker
										name="stepTimelineAndPricing.endDate"
										label="Thời gian kết thúc"
										slotProps={{
											textField: {
												required: true,
											},
										}}
										disablePast
										minDateTime={dayjs(startDate).add(15, "minute")}
									/>
								</Stack>
							</Stack>
							{isOffline ? (
								<>
									<TicketTierForm prefixName="stepTimelineAndPricing.ticketTiers" />
									<Stack spacing={1}>
										<InputLabelWizard required>
											Địa điểm tổ chức
										</InputLabelWizard>
										<AddressField name={`stepTimelineAndPricing.address`} />
									</Stack>
								</>
							) : (
								<>
									<Stack>
										<Stack spacing={2}>
											<InputLabelWizard>Giá vé</InputLabelWizard>
											<Stack spacing={1}>
												<Field.Switch
													name={`${PRICING_ONLINE_PREFIX_NAME}.isUnlimitedAudiences`}
													label={
														<Typography fontWeight={600} variant="body2">
															Không giới hạn sức chứa
														</Typography>
													}
												/>
												{isUnlimitedAudiences ? (
													<TextField
														label="Số người đăng ký"
														required
														value="Không giới hạn"
														disabled
													/>
												) : (
													<RHFInputDecimalField
														name={`${PRICING_ONLINE_PREFIX_NAME}.limitAudiences`}
														label="Số người đăng ký"
														required
													/>
												)}
											</Stack>
											<Stack spacing={1}>
												<Field.Switch
													name={`${PRICING_ONLINE_PREFIX_NAME}.isFree`}
													disabled={hasBeenPaid}
													label={
														<Typography fontWeight={600} variant="body2">
															Vé miễn phí
														</Typography>
													}
												/>
												<RHFInputDecimalField
													disabled={isFree || hasBeenPaid}
													label="Giá vé"
													required
													name={`${PRICING_ONLINE_PREFIX_NAME}.farePrice`}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end"> ₫</InputAdornment>
														),
													}}
													sx={{
														flex: 1,
														textAlign: "right",
													}}
												/>
											</Stack>
										</Stack>
									</Stack>
									<Stack spacing={3}>
										<InputLabelWizard>Link tham gia</InputLabelWizard>
										<EventRoomSelect
											prefixName="stepTimelineAndPricing"
											isFree={isFree}
											roomProvider={roomProvider}
										/>
									</Stack>
								</>
							)}
							<SocialCommunitySection prefixName="stepTimelineAndPricing" />
						</Stack>
					</CardContent>
				</CardWizard>

				{navigator}
			</Box>
			{!mdDown && <PreviewInfo data={data as WizardSchemaType} />}
		</Stack>
	);
};

export default StepTimelineAndPricing;
